import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAudio } from '../contexts/AudioContext';
import './MiniAudioPlayer.css';

// Playback speed options
const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

export function MiniAudioPlayer() {
  const { i18n } = useTranslation();
  const {
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    playbackSpeed,
    isLoading,
    error,
    togglePlay,
    stop,
    seek,
    setVolume,
    toggleMute,
    setPlaybackSpeed,
    skip,
    retry,
    showMiniPlayer,
    setShowMiniPlayer,
  } = useAudio();

  const isRTL = i18n.language === 'fa';
  const displayTitle = currentTrack 
    ? (isRTL && currentTrack.titleFa ? currentTrack.titleFa : currentTrack.title)
    : '';

  // Format time as MM:SS
  const formatTime = (seconds: number): string => {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
    
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle progress bar click
  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = percentage * duration;
    seek(newTime);
  }, [duration, seek]);

  // Handle volume change
  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setVolume(parseFloat(e.target.value));
  }, [setVolume]);

  // Cycle through playback speeds
  const cyclePlaybackSpeed = useCallback(() => {
    const currentIndex = PLAYBACK_SPEEDS.indexOf(playbackSpeed);
    const nextIndex = (currentIndex + 1) % PLAYBACK_SPEEDS.length;
    setPlaybackSpeed(PLAYBACK_SPEEDS[nextIndex]);
  }, [playbackSpeed, setPlaybackSpeed]);

  // Close mini player
  const handleClose = useCallback(() => {
    stop();
    setShowMiniPlayer(false);
  }, [stop, setShowMiniPlayer]);

  if (!showMiniPlayer || !currentTrack) {
    return null;
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Error state
  if (error) {
    return (
      <div className={`mini-audio-player mini-audio-player-error ${isRTL ? 'rtl' : 'ltr'}`}>
        <div className="map-content">
          <div className="map-info">
            <span className="map-error-icon">⚠️</span>
            <div className="map-track-info">
              <span className="map-error-message">
                {isRTL ? 'خطا در بارگذاری صوت' : 'Error loading audio'}
              </span>
              <span className="map-title map-title-error">{displayTitle}</span>
            </div>
          </div>
          <div className="map-controls">
            <button 
              className="map-btn map-btn-retry"
              onClick={retry}
              title={isRTL ? 'تلاش مجدد' : 'Retry'}
            >
              🔄 {isRTL ? 'تلاش مجدد' : 'Retry'}
            </button>
            <button 
              className="map-btn map-btn-close"
              onClick={handleClose}
              title={isRTL ? 'بستن' : 'Close'}
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`mini-audio-player ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Progress Bar */}
      <div 
        className="map-progress-container"
        onClick={handleProgressClick}
      >
        <div className="map-progress-bar">
          <div className="map-progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="map-content">
        {/* Track Info */}
        <div className="map-info">
          <span className="map-icon">🎧</span>
          <div className="map-track-info">
            <span className="map-title">{displayTitle}</span>
            <span className="map-time">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
        </div>

        {/* Controls */}
        <div className="map-controls">
          {/* Skip Back */}
          <button 
            className="map-btn map-btn-small"
            onClick={() => skip(-10)}
            title={isRTL ? '۱۰ ثانیه به عقب' : '-10s'}
          >
            ⏪
          </button>

          {/* Play/Pause */}
          <button 
            className="map-btn map-btn-play"
            onClick={togglePlay}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="map-spinner"></span>
            ) : isPlaying ? (
              '⏸️'
            ) : (
              '▶️'
            )}
          </button>

          {/* Skip Forward */}
          <button 
            className="map-btn map-btn-small"
            onClick={() => skip(10)}
            title={isRTL ? '۱۰ ثانیه به جلو' : '+10s'}
          >
            ⏩
          </button>

          {/* Speed */}
          <button 
            className="map-btn map-btn-speed"
            onClick={cyclePlaybackSpeed}
            title={isRTL ? 'سرعت پخش' : 'Playback speed'}
          >
            {playbackSpeed}x
          </button>

          {/* Volume */}
          <div className="map-volume">
            <button 
              className="map-btn map-btn-small"
              onClick={toggleMute}
            >
              {isMuted || volume === 0 ? '🔇' : volume < 0.5 ? '🔉' : '🔊'}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              className="map-volume-slider"
            />
          </div>

          {/* Close */}
          <button 
            className="map-btn map-btn-close"
            onClick={handleClose}
            title={isRTL ? 'بستن' : 'Close'}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
